import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup
} from '@mui/material';
import {
  Psychology as AiIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  CloudUpload as UploadIcon,
  PlayArrow as StartIcon,
  Save as SaveIcon,
  AutoAwesome as AutoIcon,
  Chat as ChatIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { HEADERS } from '../../../../../API/UrlAndPaths';

const AI_STEPS = [
  'Setup',
  'Import Data',
  'Review & Match',
  'Set Quantities',
  'Complete'
];

const AiAssistantDialog = ({
  open,
  onClose,
  // Form data and setters
  branchList = [],
  locationList: initialLocationList = [],
  selectedBranch,
  setSelectedBranch,
  selectedLocation,
  setSelectedLocation,
  tableData = [],
  setTableData,
  setAiImportActive,  // Flag to prevent tableList from overwriting AI import
  // Optional full product list (used by Branch / Internal Transfer to get stock)
  productList = [],
  // API functions
  onSave,
  onStart,
  onValidate,
  // Fetch location function
  fetchLocations,
  // Other props
  operationType = 'adjustment',
  singleViewById,
  status
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aiMessage, setAiMessage] = useState('');
  const [locationList, setLocationList] = useState(initialLocationList || []);
  const [userInput, setUserInput] = useState('');
  
  // Import state
  const [file, setFile] = useState(null);
  const [importedItems, setImportedItems] = useState([]);
  const [matchResults, setMatchResults] = useState([]);
  
  // Quantity state
  const [quantityMode, setQuantityMode] = useState('same'); // 'same', 'custom', 'fromFile'
  const [variations, setVariations] = useState([]);
  
  // Auto-complete state
  const [autoComplete, setAutoComplete] = useState(false);
  // Last stock lookup so user can say "add in table"
  const [lastStockMatch, setLastStockMatch] = useState(null); // { productName, qty, sourceRow, operationType }

  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setAiMessage("👋 Hello! I'm your AI assistant. I'll help you complete this inventory adjustment.\n\nLet's start by selecting the branch and location.");
    }
  }, [open]);

  // Fetch locations when branch changes
  useEffect(() => {
    if (selectedBranch?._id) {
      // Fetch locations for the selected branch
      const fetchLocs = async () => {
        try {
          console.log('🔍 Fetching locations for branch:', selectedBranch._id);
          const response = await axios.post('inventory/listwarehouselocs', {
            branchId: selectedBranch._id
          }, HEADERS);
          
          console.log('📍 Locations response:', response.data);
          if (response.data && Array.isArray(response.data)) {
            setLocationList(response.data);
          } else if (response.data) {
            // Handle if data is wrapped in an object
            setLocationList(response.data.data || response.data);
          }
        } catch (err) {
          console.error('Error fetching locations:', err);
          setLocationList([]);
        }
      };
      fetchLocs();
    } else {
      setLocationList([]);
    }
  }, [selectedBranch]);

  // Update location list from props when it changes
  useEffect(() => {
    if (initialLocationList?.length > 0 && locationList.length === 0) {
      setLocationList(initialLocationList);
    }
  }, [initialLocationList]);

  const isTransfer = operationType === 'branch-transfer' || operationType === 'internal-transfer';

  // Helper: normalize names
  const normalizeText = (name) => {
    if (!name) return '';
    return name.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
  };

  // AI Chat function
  const sendToAi = async (message) => {
    setUserInput('');
    setAiMessage(prev => prev + `\n\n**You:** ${message}`);
    
    // Simple command parsing
    const lowerMsg = message.toLowerCase();
    
    // Branch selection
    if (lowerMsg.includes('branch') || lowerMsg.includes('select')) {
      const branchMatch = branchList.find(b => 
        lowerMsg.includes(b.branchName?.toLowerCase()) || 
        lowerMsg.includes(b.branchCode?.toLowerCase())
      );
      if (branchMatch) {
        setSelectedBranch(branchMatch);
        setAiMessage(prev => prev + `\n\n**AI:** ✅ Selected branch: ${branchMatch.branchName}`);
        return;
      }
    }
    
    // Stock questions: e.g. "how much farooka stock" / "stock for farooka"
    if (lowerMsg.includes('stock') || lowerMsg.includes('qty') || lowerMsg.includes('quantity')) {
      // If branch/location not yet chosen, try to infer from the question text
      let autoBranch = selectedBranch;
      let autoLocation = selectedLocation;

      if (!autoBranch && Array.isArray(branchList) && branchList.length > 0) {
        autoBranch = branchList.find(b => {
          const bName = b.branchName?.toLowerCase() || '';
          const bCode = b.branchCode?.toLowerCase() || '';
          return (bName && lowerMsg.includes(bName)) || (bCode && lowerMsg.includes(bCode));
        });
        if (autoBranch) {
          setSelectedBranch(autoBranch);
        }
      }

      if (!autoLocation && Array.isArray(locationList) && locationList.length > 0) {
        autoLocation = locationList.find(l => {
          const lname = (l.locationName || l.name || '').toLowerCase();
          return lname && lowerMsg.includes(lname);
        });
        if (autoLocation) {
          setSelectedLocation(autoLocation);
        }
      }

      // Try to answer from existing data for current operation type
      const words = lowerMsg.split(/\s+/);
      // Remove common non-product words
      const blacklist = ['how', 'much', 'stock', 'qty', 'quantity', 'in', 'our', 'branch', 'branches', 'location', 'store', 'do', 'we', 'have', 'has', 'is', 'the'];
      const productHint = words.filter(w => !blacklist.includes(w)).join(' ');
      const normalizedHint = normalizeText(productHint);

      // Helper to check name match
      const nameMatches = (candidate) => {
        const n = normalizeText(candidate);
        if (!n || !normalizedHint) return false;
        return n === normalizedHint || n.includes(normalizedHint) || normalizedHint.includes(n);
      };

      let sourceRow = null;

      if (isTransfer) {
        // For transfers prefer productList (same as main UI)
        if (productList && productList.length > 0) {
          sourceRow = productList.find(p => nameMatches(p.productName || p.name));
        }
        // Fallback to tableData
        if (!sourceRow && tableData && tableData.length > 0) {
          sourceRow = tableData.find(r => nameMatches(r.productName || r.product?.productName));
        }
      } else {
        // Inventory adjustment: prefer full productList first (all products), then tableData from Start
        if (productList && productList.length > 0) {
          sourceRow = productList.find(p => nameMatches(p.productName || p.name));
        }
        if (!sourceRow && tableData && tableData.length > 0) {
          sourceRow = tableData.find(r => nameMatches(r.productName || r.product?.productName));
        }
      }

      if (!sourceRow) {
        setAiMessage(prev => prev + `\n\n**AI:** ⚠️ I couldn't find that product in the current list. Please make sure you've selected the correct **Branch & Location** and clicked **Start & Load Products**.`);
        return;
      }

      const branchName = autoBranch?.branchName || selectedBranch?.branchName || 'selected branch';
      const locationName = autoLocation?.name || selectedLocation?.name || 'selected location';

      let qtyValue = 0;
      if (isTransfer) {
        // Transfers: use productList / tableData quantity directly
        qtyValue = Number(sourceRow.onHandQty ?? sourceRow.stock ?? sourceRow.qty ?? 0) || 0;
      } else {
        // Inventory Adjustment:
        // 1) Start from whatever sourceRow has
        qtyValue = Number(sourceRow.stock ?? sourceRow.onHandQty ?? sourceRow.qty ?? 0) || 0;

        // 2) If the same product exists in the main adjustment table, prefer that row's stock
        if (tableData && tableData.length > 0) {
          const tableRow = tableData.find(r =>
            nameMatches(r.productName || r.product?.productName)
          );
          if (tableRow) {
            qtyValue = Number(tableRow.stock ?? tableRow.onHandQty ?? tableRow.qty ?? 0) || qtyValue;
          }
        }
      }

      const qtyLabel = isTransfer ? 'On Hand Qty' : 'Real Qty';
      const productName = sourceRow.productName || sourceRow.product?.productName || productHint || 'the product';

      setLastStockMatch({
        operationType,
        productName,
        onHand: qtyValue,
        sourceRow,
      });

      setAiMessage(prev => prev + `\n\n**AI:** 📊 I found **${productName.toUpperCase()}** in **${branchName} / ${locationName}** with **${qtyLabel} = ${qtyValue}**.\nYou can say **"add this to table"** if you want me to insert it into the lines.`);
      return;
    }

    // Add last queried product into table
    if (lowerMsg.includes('add') && lowerMsg.includes('table') && lastStockMatch) {
      const { productName, onHand, sourceRow } = lastStockMatch;

      if (isTransfer) {
        // Branch / Internal Transfer line
        const newRow = {
          type: 'lines',
          id: Date.now() + Math.random(),
          product: sourceRow.product || {
            _id: sourceRow._id,
            productName: productName,
            uom: sourceRow.uom,
            uomName: sourceRow.uomName,
          },
          des: sourceRow.des || productName,
          // Show human-readable UOM in the grid (same as normal dropdown behaviour)
          uom: sourceRow.uomName || sourceRow.uom || '',
          uomName: sourceRow.uomName || '',
          spMargin: sourceRow.spMargin || '',
          onHandQty: Number(onHand) || 0,
          transferQty: 0,
          _id: sourceRow._id,
          posCat: sourceRow.posCat || sourceRow.poscat || '',
          imported: true,
        };

        setTableData(prev => [...prev, newRow]);
        setAiMessage(prev => prev + `\n\n**AI:** ✅ Added **${productName}** to the table with On Hand Qty = ${onHand} and Transfer Qty = 0.`);
      } else {
        // Inventory Adjustment line
        const newRow = {
          type: 'lines',
          id: Date.now() + Math.random(),
          product: sourceRow.product || null,
          productName,
          uom: sourceRow.uom || '',
          uomName: sourceRow.uomName || '',
          location: sourceRow.location || selectedLocation?.name || '',
          stock: Number(onHand) || 0,
          realQty: 0,
          scannedQty: 0,
          imported: true,
        };

        setTableData(prev => [...prev, newRow]);
        setAiMessage(prev => prev + `\n\n**AI:** ✅ Added **${productName}** to the table with On Hand Qty = ${onHand}. Real Qty is set to 0 – you can change it.`);
      }

      return;
    }

    // Location selection
    if (lowerMsg.includes('location')) {
      const locMatch = locationList.find(l => 
        lowerMsg.includes(l.locationName?.toLowerCase())
      );
      if (locMatch) {
        setSelectedLocation(locMatch);
        setAiMessage(prev => prev + `\n\n**AI:** ✅ Selected location: ${locMatch.locationName}`);
        return;
      }
    }
    
    // Start command
    if (lowerMsg.includes('start') || lowerMsg.includes('begin') || lowerMsg.includes('load')) {
      if (selectedBranch && selectedLocation) {
        setAiMessage(prev => prev + `\n\n**AI:** 🚀 Starting inventory adjustment and loading products...`);
        await handleStart();
        return;
      } else {
        setAiMessage(prev => prev + `\n\n**AI:** ⚠️ Please select branch and location first.`);
        return;
      }
    }
    
    // Complete command
    if (lowerMsg.includes('complete') || lowerMsg.includes('finish') || lowerMsg.includes('done')) {
      setAiMessage(prev => prev + `\n\n**AI:** 📝 Let me complete this for you...`);
      await handleAutoComplete();
      return;
    }
    
    // Same as on hand
    if (lowerMsg.includes('same') && lowerMsg.includes('on hand')) {
      setQuantityMode('same');
      setAiMessage(prev => prev + `\n\n**AI:** ✅ I'll set all Real Qty same as On Hand Qty.`);
      return;
    }
    
    // Help
    setAiMessage(prev => prev + `\n\n**AI:** I can help you with:
• **Select branch [name]** - Select a branch
• **Select location [name]** - Select a location  
• **Start** - Load all products
• **How much [product] stock** - Show On Hand Qty for that product in the selected branch/location
• **Add this to table** - Insert the last-queried product into the lines
• **Same as on hand** - Set Real/Transfer Qty = On Hand Qty (depending on screen)
• **Complete** - Auto-complete the adjustment/transfer`);
  };

  const handleStart = async () => {
    if (!selectedBranch || !selectedLocation) {
      setAiMessage(prev => prev + `\n\n**AI:** ⚠️ Please select both branch and location first.`);
      return;
    }
    
    setLoading(true);
    try {
      // For transfers (no onSave/onStart), skip directly to import step
      if (!onSave && !onStart) {
        setAiMessage(prev => prev + `\n\n**AI:** ✅ Branch and location selected! Now you can import data.`);
        setCurrentStep(1);
        setLoading(false);
        return;
      }
      
      // First, save the adjustment (creates the record)
      setAiMessage(prev => prev + `\n\n**AI:** 💾 Saving adjustment first...`);
      if (onSave) {
        await onSave(selectedBranch, selectedLocation);
        // Wait longer for save to complete and ID to be set
        setAiMessage(prev => prev + `\n\n**AI:** ⏳ Waiting for save to complete...`);
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
      
      // Then start (loads products)
      setAiMessage(prev => prev + `\n\n**AI:** 🚀 Starting and loading products...`);
      if (onStart) {
        // Pass the selected values directly to onStart
        await onStart(selectedBranch, selectedLocation);
        setAiMessage(prev => prev + `\n\n**AI:** ✅ Products loaded! Now you can import data or set quantities.`);
        setCurrentStep(1);
      }
    } catch (err) {
      setAiMessage(prev => prev + `\n\n**AI:** ❌ Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;
    
    setFile(uploadedFile);
    setLoading(true);
    setAiMessage(prev => prev + `\n\n**AI:** 📄 Processing file: ${uploadedFile.name}...`);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('operationType', operationType);
      // Send branchId and locationId for stock lookup
      if (selectedBranch?._id) {
        formData.append('branchId', selectedBranch._id);
      }
      if (selectedLocation?._id) {
        formData.append('locationId', selectedLocation._id);
      }
      console.log('📤 Sending to backend:', { 
        branchId: selectedBranch?._id, 
        locationId: selectedLocation?._id,
        operationType 
      });
      
      const token = axios.defaults.headers.common['Authorization'];
      let baseURL = axios.defaults.baseURL || '';
      if (baseURL.endsWith('/')) baseURL = baseURL.slice(0, -1);
      
      const response = await fetch(`${baseURL}/api/inventory-ai/parse-document`, {
        method: 'POST',
        headers: { 'Authorization': token },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        setImportedItems(data.data.items || []);
        setMatchResults(data.data.items || []);
        
        const matched = data.data.items.filter(i => i.matchedProduct).length;
        const total = data.data.items.length;
        
        setAiMessage(prev => prev + `\n\n**AI:** ✅ Found ${total} items! ${matched} matched to products.`);
        setCurrentStep(2);
      } else {
        setAiMessage(prev => prev + `\n\n**AI:** ❌ ${data.message}`);
      }
    } catch (err) {
      setAiMessage(prev => prev + `\n\n**AI:** ❌ Error processing file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const applyImportedData = () => {
    // Helper to normalize names for matching
    const normalizeName = (name) => {
      if (!name) return '';
      return name.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
    };
    
    const namesMatch = (name1, name2) => {
      const n1 = normalizeName(name1);
      const n2 = normalizeName(name2);
      if (!n1 || !n2) return false;
      if (n1 === n2) return true;
      if (n1.includes(n2) || n2.includes(n1)) return true;
      return false;
    };
    
    // For transfers, we ADD new rows from scanned data
    
    // Find matching rows from table for each scanned item
    const matchedRows = [];
    const notFoundProducts = [];
    
    // Log first row to see field names
    if (tableData.length > 0) {
      console.log('📋 Sample tableData row fields:', Object.keys(tableData[0]));
      console.log('📋 Sample tableData row:', tableData[0]);
    }
    
    matchResults.forEach(item => {
      const itemName = item.matchedProduct?.productName || item.productName || '';
      const scannedQty = item.quantity || 0;
      
      // Find matching row in current table
      const matchingRow = tableData.find(row => {
        const rowName = row.productName || row.product?.productName || '';
        return namesMatch(itemName, rowName);
      });
      
      // For transfers we require a full product object, for adjustments we accept any matching row
      const hasValidMatch = isTransfer ? (matchingRow && matchingRow.product) : !!matchingRow;
      
      if (hasValidMatch) {
        // Get on hand qty - try different field names
        const onHandQty = matchingRow.onHandQty || matchingRow.stock || matchingRow.qty || matchingRow.quantity || 0;
        console.log(`✅ Found in table: "${itemName}" - On Hand: ${onHandQty}, Scanned Qty: ${scannedQty}`);
        
        // Create new row - explicitly set qty AFTER spread to ensure it's not overwritten
        const newRow = {
          ...matchingRow
        };
        // For transfers, set transferQty and onHandQty; for adjustments, set realQty
        if (isTransfer) {
          newRow.transferQty = Number(scannedQty) || 0;
          newRow.onHandQty = Number(onHandQty) || 0;
        } else {
          newRow.realQty = Number(scannedQty) || 0;
          newRow.scannedQty = Number(scannedQty) || 0;
          newRow.stock = Number(onHandQty) || 0;
        }
        newRow.imported = true;
        
        console.log('📋 New row qty AFTER assignment:', isTransfer ? newRow.transferQty : newRow.realQty, 'onHandQty:', newRow.onHandQty);
        matchedRows.push(newRow);
      } else if (item.matchedProduct) {
        // For transfers: create new row from matched product even if not in current table
        // Prefer stock and meta from branch-transfer productList (same source as manual UI),
        // fall back to values returned by backend match.
        let productStock = item.matchedProduct.onHandQty || item.matchedProduct.stock || item.matchedProduct.qty || 0;
        let srcForMeta = null;

        if (isTransfer && productList?.length) {
          const byId = productList.find(p => p?._id === item.matchedProduct?._id);
          const byProdId = !byId && item.matchedProduct?.prodId != null
            ? productList.find(p => p?.prodId === item.matchedProduct.prodId)
            : null;
          srcForMeta = byId || byProdId;
          if (srcForMeta && (!productStock || productStock === 0)) {
            productStock = srcForMeta.onHandQty ?? srcForMeta.stock ?? srcForMeta.qty ?? productStock;
          }
        }

        console.log(`➕ Adding from matched product: "${itemName}" transferQty: ${scannedQty}, onHandQty: ${productStock}`);
        
        // Build row based on operation type
        let newRow;
        if (isTransfer) {
          // Branch Transfer / Internal Transfer row structure
          const meta = srcForMeta || {};
          const isInternalTransfer = operationType === 'internal-transfer';

          newRow = {
            type: 'lines',
            id: Date.now() + Math.random(),
            product: item.matchedProduct,
            // Description
            des: meta.des || item.matchedProduct.description || item.matchedProduct.productName || '',
            // UOM:
            // - Branch Transfer: keep existing behaviour (uom may be id, uomName is label)
            // - Internal Transfer: table shows row.uom as readable text, so prefer uomName there
            uom: isInternalTransfer
              ? (meta.uomName || item.matchedProduct.uomName || meta.uom || item.matchedProduct.uom || '')
              : (meta.uom || item.matchedProduct.uom || ''),
            uomName: meta.uomName || item.matchedProduct.uomName || '',
            spMargin: meta.spMargin || item.matchedProduct.spMargin || '',
            onHandQty: Number(productStock) || 0,
            transferQty: Number(scannedQty) || 0,
            _id: item.matchedProduct._id || meta._id || '',
            posCat: meta.posCat || item.matchedProduct.posCat || '',
            imported: true
          };
        } else {
          // Inventory Adjustment row structure
          newRow = {
            type: 'lines',
            id: Date.now() + Math.random(),
            product: item.matchedProduct,
            productName: item.matchedProduct.productName,
            uom: item.matchedProduct.uom || '',
            uomName: item.matchedProduct.uomName || '',
            stock: Number(productStock) || 0,
            realQty: Number(scannedQty) || 0,
            scannedQty: Number(scannedQty) || 0,
            imported: true
          };
        }
        matchedRows.push(newRow);
      } else {
        console.log(`❌ Not found: "${itemName}"`);
        notFoundProducts.push(itemName);
      }
    });
    
    console.log(`📊 Matched ${matchedRows.length} products, Not found: ${notFoundProducts.length}`);
    
    if (matchedRows.length > 0) {
      // Create a deep copy to avoid reference issues
      const finalRows = matchedRows.map(row => ({...row}));

      console.log('🔴 Setting tableData with finalRows:', finalRows);

      // Set flag FIRST to prevent any resets, then set data
      if (setAiImportActive) {
        setAiImportActive(true);
      }

      // Set table data with a small delay to ensure flag is set first
      setTimeout(() => {
        console.log('🔴 Inside setTimeout - setting tableData now');
        if (isTransfer) {
          // For transfers, add to existing table (filter out empty rows first)
          setTableData(prev => {
            const existingRows = prev.filter(row => row.product && row.product._id);
            return [...existingRows, ...finalRows];
          });
        } else {
          // For adjustments, replace table with matched rows only
          setTableData(finalRows);
        }
      }, 100);
      
      const qtyField = isTransfer ? 'Transfer Qty' : 'Real Qty';
      const qtyValue = isTransfer ? finalRows[0]?.transferQty : finalRows[0]?.realQty;
      setAiMessage(prev => prev + `\n\n**AI:** ✅ Added ${matchedRows.length} products with ${qtyField} = ${qtyValue}!`);
    }
    
    if (notFoundProducts.length > 0) {
      const branchName = selectedBranch?.branchName || 'selected branch';
      const locationName = selectedLocation?.name || 'selected location';
      setAiMessage(prev => prev + `\n\n**AI:** ⚠️ Products not found in ${branchName} / ${locationName}: ${notFoundProducts.join(', ')}`);
    }
    
    setCurrentStep(3);
  };

  const setAllSameAsOnHand = () => {
    const updatedTable = tableData.map(row => ({
      ...row,
      realQty: row.onHandQty || 0
    }));
    setTableData(updatedTable);
    setAiMessage(prev => prev + `\n\n**AI:** ✅ Set all Real Qty same as On Hand Qty.`);
  };

  const calculateVariations = () => {
    const vars = tableData.filter(row => {
      const onHand = parseFloat(row.onHandQty) || 0;
      const real = parseFloat(row.realQty) || 0;
      return onHand !== real;
    }).map(row => ({
      ...row,
      variance: (parseFloat(row.realQty) || 0) - (parseFloat(row.onHandQty) || 0)
    }));
    
    setVariations(vars);
    return vars;
  };

  const handleAutoComplete = async () => {
    setLoading(true);
    
    try {
      // Calculate variations
      const vars = calculateVariations();
      
      if (vars.length > 0) {
        const result = await Swal.fire({
          icon: 'info',
          title: 'Variations Found',
          html: `
            <p><strong>${vars.length}</strong> products have quantity differences:</p>
            <div style="max-height: 200px; overflow-y: auto; text-align: left; font-size: 12px;">
              ${vars.slice(0, 10).map(v => `
                <p>• ${v.productName}: ${v.onHandQty} → ${v.realQty} (${v.variance > 0 ? '+' : ''}${v.variance})</p>
              `).join('')}
              ${vars.length > 10 ? `<p>... and ${vars.length - 10} more</p>` : ''}
            </div>
            <p>Do you want me to complete the adjustment?</p>
          `,
          showCancelButton: true,
          confirmButtonText: 'Yes, Complete',
          cancelButtonText: 'Review First'
        });
        
        if (!result.isConfirmed) {
          setCurrentStep(3);
          setLoading(false);
          return;
        }
      }
      
      // Save first
      setAiMessage(prev => prev + `\n\n**AI:** 💾 Saving adjustment...`);
      if (onSave) {
        await onSave();
      }
      
      // Then validate/post
      setAiMessage(prev => prev + `\n\n**AI:** ✅ Validating and completing...`);
      if (onValidate) {
        await onValidate();
      }
      
      setAiMessage(prev => prev + `\n\n**AI:** 🎉 Inventory adjustment completed successfully!`);
      setCurrentStep(4);
      
      setTimeout(() => {
        Swal.fire({
          icon: 'success',
          title: 'Completed!',
          text: 'Inventory adjustment has been completed.',
          timer: 2000,
          showConfirmButton: false
        });
      }, 500);
      
    } catch (err) {
      setAiMessage(prev => prev + `\n\n**AI:** ❌ Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Setup
        return (
          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Step 1: Select Branch & Location
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Autocomplete
                fullWidth
                options={branchList || []}
                getOptionLabel={(opt) => opt?.branchName || ''}
                value={selectedBranch}
                onChange={(e, val) => setSelectedBranch(val)}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                renderOption={(props, option) => (
                  <li {...props} key={option?._id || Math.random()}>
                    {option?.branchName || ''}
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Branch" size="small" />}
              />
              
              <Autocomplete
                fullWidth
                options={locationList || []}
                getOptionLabel={(opt) => opt?.name || ''}
                value={selectedLocation}
                onChange={(e, val) => setSelectedLocation(val)}
                isOptionEqualToValue={(option, value) => option?._id === value?._id}
                renderOption={(props, option) => (
                  <li {...props} key={option?._id || Math.random()}>
                    {option?.name || ''}
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Location" size="small" />}
              />
            </Box>
            
            <Button
              variant="contained"
              startIcon={<StartIcon />}
              onClick={handleStart}
              disabled={!selectedBranch || !selectedLocation || loading}
            >
              Start & Load Products
            </Button>
          </Box>
        );
        
      case 1: // Import
        return (
          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Step 2: Import Data (Optional)
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              Upload an Excel, Image, or PDF file with product quantities. Or skip to set quantities manually.
            </Alert>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
              >
                Upload File
                <input type="file" hidden onChange={handleFileUpload} accept=".xlsx,.xls,.csv,.png,.jpg,.jpeg,.pdf" />
              </Button>
              
              {file && <Chip label={file.name} onDelete={() => setFile(null)} />}
            </Box>
            
            <Button
              variant="text"
              onClick={() => setCurrentStep(3)}
            >
              Skip Import →
            </Button>
          </Box>
        );
        
      case 2: // Review matches
        // Helper to find On Hand Qty
        const getOnHandQty = (item) => {
          // For Branch/Internal Transfer, prefer productList (same source as normal dropdown)
          if (isTransfer) {
            if (item.matchedProduct?._id && productList?.length) {
              const byId = productList.find(p => p?._id === item.matchedProduct._id);
              const byProdId = !byId && item.matchedProduct?.prodId != null
                ? productList.find(p => p?.prodId === item.matchedProduct.prodId)
                : null;
              const src = byId || byProdId;
              if (src) {
                return src.onHandQty ?? src.stock ?? src.qty ?? 0;
              }
            }
            // Fallback to values already present on matchedProduct
            return item.matchedProduct?.onHandQty ?? item.matchedProduct?.stock ?? 0;
          }

          // Inventory Adjustment: look into existing tableData (unchanged behaviour)
          const itemName = item.matchedProduct?.productName || item.productName;
          const normalizeName = (name) => name?.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim() || '';
          const normalizedItemName = normalizeName(itemName);
          const matchingRow = tableData.find(row => {
            const rowName = normalizeName(row.productName || row.product?.productName);
            return rowName && normalizedItemName && (rowName === normalizedItemName || rowName.includes(normalizedItemName) || normalizedItemName.includes(rowName));
          });
          
          if (matchingRow) {
            return matchingRow.onHandQty ?? matchingRow.stock ?? matchingRow.qty ?? matchingRow.quantity ?? 0;
          }
          
          return 0;
        };
        
        return (
          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {isTransfer ? 'Step 3: Review Imported Transfer Data' : 'Step 3: Review Imported Data'}
            </Typography>
            
            <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Matched To</TableCell>
                    <TableCell align="right">On Hand Qty</TableCell>
                    <TableCell align="right">
                      {isTransfer ? 'Transfer Qty (Scanned)' : 'Real Qty (Scanned)'}
                    </TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matchResults.map((item, idx) => {
                    const onHandQty = getOnHandQty(item);
                    return (
                      <TableRow key={idx}>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>{item.matchedProduct?.productName || '-'}</TableCell>
                        <TableCell align="right">{onHandQty}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          {item.quantity}
                        </TableCell>
                        <TableCell>
                          {item.matchedProduct ? (
                            <Chip size="small" label="Matched" color="success" />
                          ) : (
                            <Chip size="small" label="Unmatched" color="warning" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Button
              variant="contained"
              onClick={applyImportedData}
              startIcon={<CheckIcon />}
            >
              Apply to Products
            </Button>
          </Box>
        );
        
      case 3: // Set quantities
        return (
          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {isTransfer ? 'Step 4: Set Transfer Quantities' : 'Step 4: Set Real Quantities'}
            </Typography>
            
            <RadioGroup
              value={quantityMode}
              onChange={(e) => setQuantityMode(e.target.value)}
              sx={{ mb: 2 }}
            >
              <FormControlLabel 
                value="same" 
                control={<Radio />} 
                label={isTransfer ? 'Set all Transfer Qty same as On Hand Qty (no changes)' : 'Set all Real Qty same as On Hand Qty (no changes)'}
              />
              <FormControlLabel 
                value="fromFile" 
                control={<Radio />} 
                label={isTransfer ? 'Use transfer quantities from imported file' : 'Use quantities from imported file'}
                disabled={importedItems.length === 0}
              />
              <FormControlLabel 
                value="custom" 
                control={<Radio />} 
                label="I'll set quantities manually in the form"
              />
            </RadioGroup>
            
            {quantityMode === 'same' && (
              <Button
                variant="contained"
                onClick={() => {
                  setAllSameAsOnHand();
                  setCurrentStep(4);
                }}
                startIcon={<AutoIcon />}
              >
                Apply & Continue
              </Button>
            )}
            
            {quantityMode === 'fromFile' && (
              <Button
                variant="contained"
                onClick={() => setCurrentStep(4)}
                startIcon={<CheckIcon />}
              >
                Continue with Imported Data
              </Button>
            )}
            
            {quantityMode === 'custom' && (
              <Button
                variant="outlined"
                onClick={onClose}
              >
                Close & Edit Manually
              </Button>
            )}
          </Box>
        );
        
      case 4: // Complete
        return (
          <Box>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Step 5: Complete Adjustment
            </Typography>
            
            {variations.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>{variations.length} products</strong> have quantity variations.
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="success"
                onClick={handleAutoComplete}
                startIcon={<AutoIcon />}
                disabled={loading}
              >
                Complete Automatically
              </Button>
              
              <Button
                variant="outlined"
                onClick={onClose}
              >
                Review in Form
              </Button>
            </Box>
          </Box>
        );
        
      default:
        return null;
    }
  };

  // Get title based on operation type
  const getDialogTitle = () => {
    switch (operationType) {
      case 'branch-transfer':
        return 'AI Assistant - Branch Transfer';
      case 'internal-transfer':
        return 'AI Assistant - Internal Transfer';
      case 'adjustment':
      default:
        return 'AI Assistant - Inventory Adjustment';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AiIcon color="primary" />
        {getDialogTitle()}
        <IconButton onClick={onClose} sx={{ ml: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        {/* Stepper */}
        <Stepper activeStep={currentStep} sx={{ mb: 3 }}>
          {AI_STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {/* AI Chat Area */}
        <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
          <CardContent>
            <Box sx={{ 
              maxHeight: 200, 
              overflowY: 'auto', 
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: '0.85rem'
            }}>
              {aiMessage}
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Type a command (e.g., 'start', 'complete', 'same as on hand')"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendToAi(userInput)}
              />
              <Button 
                variant="contained" 
                onClick={() => sendToAi(userInput)}
                disabled={!userInput.trim()}
              >
                Send
              </Button>
            </Box>
          </CardContent>
        </Card>
        
        {/* Step Content */}
        {renderStepContent()}
        
        {/* Loading */}
        {loading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              Processing...
            </Typography>
          </Box>
        )}
        
        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiAssistantDialog;
